/**
 * GTM dataLayer push helpers — typed for the brochure event schema.
 * Shared between all four brochure domain apps.
 */

import type { BrochureDeliveryMethod } from "../types";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    fbq?: (...args: unknown[]) => void;
  }
}

export interface BrochurePageViewEvent {
  event: "brochure_page_view";
  page_id: string;
  page_domain: string;
  /** URL slug of the niche page — "" for main */
  niche: string;
}

export interface BrochureStepEvent {
  event:
    | "brochure_step_1_complete"
    | "brochure_step_2_complete"
    | "brochure_step_3_complete";
  page_id: string;
  lead_id: string;
  event_id: string;
  niche?: string;
  delivery_method?: BrochureDeliveryMethod;
  postal_code_prefix?: string;
  solution_type?: string;
  budget_range?: string;
  timeframe?: string;
  lead_hashed_email?: string;
  /**
   * SHA-256 (lowercased, digits-only) van het telefoonnummer.
   * Gepusht op elke step zodra we de waarde hebben — gelezen door de Server
   * GTM Facebook CAPI tag via Event Data → user_data.ph.
   */
  lead_hashed_phone?: string;
  /**
   * SHA-256 (lowercased + trimmed) van first_name. Gepusht op elke step
   * zodra beschikbaar; gelezen door Server GTM CAPI tag → user_data.fn.
   */
  lead_hashed_first_name?: string;
  /**
   * SHA-256 (lowercased + spaces stripped) van postcode. Pas vanaf step 2
   * beschikbaar. Gelezen door Server GTM CAPI tag → user_data.zp.
   */
  lead_hashed_postal_code?: string;
  /**
   * Meta `_fbp` cookie waarde. Stape pikt deze ook uit het cookie via 1st-
   * party `data.badkamer-wijzer.nl`, maar we pushen 'm expliciet zodat de
   * Event Data variable in GTM een ondubbelzinnige bron heeft.
   */
  _fbp?: string;
  /** Meta `_fbc` cookie waarde (uit cookie of fbclid-reconstructie). */
  _fbc?: string;
  /** Euro-bedrag voor Smart Bidding (zie conversion-values.ts). Optioneel. */
  value?: number;
  /** ISO 4217 currency code, alleen aanwezig samen met value. */
  currency?: string;
}

/**
 * Zwaluw GTM-compatibele formSubmission shim — gepusht parallel aan
 * `brochure_step_*_complete` zodat een GTM container die op de Zwaluw
 * naming-conventie triggert (formSubmission + formName + formId) onze
 * leads ook kan oppakken zonder dat we ons eigen step-schema kwijtraken.
 *
 * Used door BrochureForm + andere conversion-fires.
 */
export interface FormSubmissionEvent {
  event: "formSubmission";
  /** Bv. "form_gratis_brochure", "form_brochure_badkamer_wijzer". */
  formName: string;
  /** Step-nummer als string ("1" / "2" / "3") — matched Gravity Forms ID convention. */
  formId: string;
  /** Dedup-id voor server-side CAPI matching. */
  event_id: string;
  page_id?: string;
  lead_id?: string;
}

/** Soft-conversion micro-events that don't fit the strict step schema. */
export interface BrochureMicroEvent {
  event:
    | "postcode_check_pass"
    | "lead_from_postcode_check"
    | "phone_click"
    | "appointment_booked";
  page_id?: string;
  event_id?: string;
  postcode_4?: string;
  click_source?: string;
  page_path?: string;
  appointment_kind?: string;
  /** Euro-bedrag voor Smart Bidding (zie conversion-values.ts). Optioneel. */
  value?: number;
  /** ISO 4217 currency code, alleen aanwezig samen met value. */
  currency?: string;
}

/**
 * Popup-impression + close + CTA-click events.
 *
 * Gebruikt door HansKazanPopup (zwaluw) en ExitIntentPopup (bw) om te meten of
 * de popup conversie oplevert of juist als hinderlijk wordt ervaren. We meten:
 *  - popup_shown    → impressie (trigger geeft aan hoe 'ie kwam)
 *  - popup_close    → dismiss (close_method = x / backdrop / esc)
 *  - popup_cta_click → primary CTA (gaat door naar form)
 *
 * Schema is bewust simpel zodat één GTM tag alles kan rapporteren en je in
 * GA4 op `popup` + `brand` dimensies kunt segmenteren.
 */
export interface BrochurePopupEvent {
  event: "popup_shown" | "popup_close" | "popup_cta_click";
  /** Naam van de popup (bv. "hans_kazan", "bw_exit_intent"). */
  popup: string;
  /** Brand-key voor cross-brand reporting ("zwaluw" / "bw" / ...). */
  brand: string;
  /** Alleen op popup_shown — wat triggerde de popup. */
  trigger?: "delay" | "exit_intent" | "always" | "mouseleave" | "mobile_idle";
  /** Alleen op popup_close / popup_cta_click — hoe de popup werd gesloten. */
  close_method?: "x" | "backdrop" | "esc" | "cta";
  /** Hoe lang de popup zichtbaar was voordat 'ie sloot (ms). */
  time_open_ms?: number;
}

export type BrochureDataLayerEvent =
  | BrochurePageViewEvent
  | BrochureStepEvent
  | BrochureMicroEvent
  | FormSubmissionEvent
  | BrochurePopupEvent;

/** Push a typed event to the GTM dataLayer. Safe to call from SSR — no-ops on server. */
export function pushDataLayer(event: BrochureDataLayerEvent): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(event as unknown as Record<string, unknown>);
}

/** SHA-256 hex hash — used for Meta advanced-matching (lowercased + trimmed) */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input.toLowerCase().trim());
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
